import Box from "@mui/material/Box";
import { Suspense, lazy, useEffect } from "react";
import { Outlet } from "react-router-dom";

import PageFallback from "@/components/Core/PageFallback";
import {
  useDeviceType,
  useGameState,
  useIsLoggedIn,
  useScrollToTopOnRouteChange,
  useShowFooter,
} from "@/hooks";
import Footer from "@/layout/Footer";
import Header from "@/layout/Header";
import { MainContent } from "@/layout/styles";
import { BootShellExit } from "@/lib/bootShell";
import { getFavoriteGameIds } from "@/services";
import { ChatRoom } from "@/views/Profile/ChatRoom";

import { MobileFooter } from "../Footer/MobileFooter";
import CasinoSidebar from "./Sidebar";

interface CasinoLayoutProps {
  hideFooter?: boolean;
}

// Lazy: the slip pulls in the sportsbook text and odds helpers; it renders once those arrive.
const BetSlip = lazy(() => import("@/layout/BetSlip"));

const CasinoLayout = ({ hideFooter = false }: CasinoLayoutProps) => {
  const { isMobile } = useDeviceType();
  const isLoggedIn = useIsLoggedIn();
  const { setFavorites } = useGameState();

  const showFooter = useShowFooter();
  const scrollRef = useScrollToTopOnRouteChange();

  useEffect(() => {
    if (!isLoggedIn) {
      setFavorites([]);
      return;
    }
    let active = true;
    getFavoriteGameIds()
      .then((ids) => {
        if (active) setFavorites(ids);
      })
      .catch((err) => console.error("Failed to sync favorites:", err));
    return () => {
      active = false;
    };
  }, [isLoggedIn]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CasinoSidebar />
      <Header />

      <MainContent id="main-content" ref={scrollRef} isMobile={isMobile}>
        <Box component="main" sx={{ minHeight: "calc(100vh - 68px)" }}>
          <Suspense fallback={<PageFallback />}>
            <BootShellExit />
            <Outlet />
          </Suspense>
        </Box>
        {!hideFooter && showFooter && <Footer />}
      </MainContent>

      <Suspense fallback={null}>
        <BetSlip />
      </Suspense>
      {showFooter && isMobile && <MobileFooter />}
      <ChatRoom />
    </Box>
  );
};

export default CasinoLayout;
